import { Injectable } from '@angular/core';
import { ProcessedRow } from '@import/interfaces';
import { ErrorNavigatorBeforeChange } from '@app/components/error-navigator/error-navigator.component';
import { EmployeeRowAndColumnService } from '@import/services/row-and-column/employee-row-and-column.service';

interface ColumnError {
    rowId: number;
    columnId: number;
    selector: string;
}

function makeRowError(rowId: number, columnId: number): ColumnError {
    return {
        rowId: rowId,
        columnId: columnId,
        selector: `row-${rowId}-cell-${columnId}`,
    };
}

@Injectable()
export class ReviewErrorService {
    errors: ColumnError[] = [];
    errorSelectors: string[] = [];

    constructor(public importerService: EmployeeRowAndColumnService) {}

    buildErrors(rows: ProcessedRow[]): void {
        const rowsWithErrors = rows.filter((row) => Object.keys(row.errors).length);

        this.errors = rowsWithErrors.reduce((acc: [], row) => {
            const rowErrors = Object.keys(row.errors).map((columnId) => makeRowError(row.id, +columnId));
            return [...acc, ...rowErrors];
        }, []);

        this.errorSelectors = this.errors.map((error) => error.selector);
    }

    getPageToPaginateTo(activeErrorIndex: number, perPage: number): number {
        const rows = this.importerService.processedRows;
        const error = this.errors[activeErrorIndex];

        const [activeErrorRow] = rows.filter((row) => row.id === error.rowId);

        if (!activeErrorRow) {
            return;
        }

        const rowIndex = rows.indexOf(activeErrorRow);
        const pageWithError = Math.ceil((rowIndex + 1) / perPage);

        return pageWithError;
    }

    findClosestError({ previousNonErrorInput, direction }: ErrorNavigatorBeforeChange): number {
        const { rowId, columnId } = previousNonErrorInput.dataset; // Get rowId and columnId from the data attributes on the input
        const searchArray = this.buildErrorSearchArray(+rowId, direction);
        const rowWithError = this.findErrorRow(searchArray, direction, +rowId, +columnId);
        const errorIndex = this.findClosestErrorIndex(rowWithError, direction, +rowId, +columnId);

        return errorIndex;
    }

    private buildErrorSearchArray(rowId: number, direction: 'NEXT' | 'PREVIOUS'): ProcessedRow[] {
        const processedRowClone = [...this.importerService.processedRows];

        if (direction === 'PREVIOUS') {
            processedRowClone.reverse();
        }

        const processedRowIndex = processedRowClone.findIndex((row) => row.id === rowId);
        return [...processedRowClone.slice(processedRowIndex), ...processedRowClone];
    }

    private findErrorRow(
        searchArray: ProcessedRow[],
        direction: string,
        rowId: number,
        columnId: number
    ): ProcessedRow {
        let errorRow: ProcessedRow = null;

        for (let i = 0; i < searchArray.length; i++) {
            const row = searchArray[i];
            const currentRowHasErrors = Object.keys(row.errors).length;

            if (currentRowHasErrors) {
                const rowErrorColumns = Object.keys(row.errors);

                /*
                 * We don't want to focus on an error in this row if there's
                 * no next errors or previous errors
                 *
                 * eg. user is focused on columnId 10 and hits "next",
                 * the current row only has an error for columnId 5, it should
                 * skipped then.
                 * */
                const currentRowHasErrorToNavigateTo =
                    direction === 'NEXT'
                        ? rowErrorColumns.some((errorColumnId) => +errorColumnId > +columnId) && row.id === +rowId
                        : rowErrorColumns.some((errorColumnId) => +errorColumnId < +columnId) && row.id === +rowId;

                if (currentRowHasErrorToNavigateTo) {
                    errorRow = row;
                    break;
                }

                if (!currentRowHasErrorToNavigateTo && row.id !== +rowId) {
                    errorRow = row;
                    break;
                }
            }
        }

        return errorRow;
    }

    private findClosestErrorIndex(row: ProcessedRow, direction: string, rowId: number, columnId: number): number {
        const errors = this.errors;
        const errorIsOnActiveRow = row.id === rowId;
        let filteredErrors = errors.filter((error) => error.rowId === row.id);

        if (errorIsOnActiveRow) {
            filteredErrors = filteredErrors.filter((error) => {
                if (direction === 'NEXT') {
                    return error.columnId > columnId;
                }
                return error.columnId < columnId;
            });
        }

        const errorColumn = direction === 'NEXT' ? filteredErrors[0] : filteredErrors[filteredErrors.length - 1];

        return errors.indexOf(errorColumn);
    }
}
